import { truncateLeftRight } from '@/portainer/filters/filters';
import { baseStackWebhookUrl } from '@/portainer/helpers/webhookHelper';
import { useStack } from '@/react/common/stacks/queries/useStack';
import { GitCommitLink } from '@/react/portainer/gitops/GitCommitLink';

import { Alert } from '@@/Alert';
import { CopyButton } from '@@/buttons';
import { Card } from '@@/Card';

export function GitReferenceCard({ stackID }: { stackID?: number }) {
  const stackQuery = useStack(stackID);
  const { GitConfig, AutoUpdate: autoUpdate } = stackQuery?.data || {};
  // helm chart path in the main summary
  const {
    ConfigHash: commitID,
    ReferenceName: reference,
    URL: repo,
  } = GitConfig || {};

  const { Interval: autoUpdateInterval, Webhook: webhook } = autoUpdate || {};
  const webhookUrl = webhook ? `${baseStackWebhookUrl()}/${webhook}` : '';
  return (
    <Card>
      <div className="form-section-title">Managed by Git</div>
      {stackQuery.isError && (
        <Alert color="error">
          Unable to load the Git settings related to this release. Please try
          again, or edit/upgrade the release to add the Git settings again.
        </Alert>
      )}
      {stackQuery.data && (
        <div
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-1 text-sm"
          data-cy="helm-git-info"
        >
          {!!repo && (
            <div className="min-w-0 flex items-baseline gap-1">
              <span className="text-muted">Repo:</span>
              <span
                className="truncate inline-block align-baseline max-w-[20rem]"
                title={repo}
                data-cy="helm-git-repo"
              >
                {repo}
              </span>
            </div>
          )}
          {!!reference && (
            <div className="min-w-0 flex items-baseline gap-1">
              <span className="text-muted">Ref:</span>
              <span
                className="truncate inline-block align-baseline"
                data-cy="helm-git-ref"
              >
                {reference}
              </span>
            </div>
          )}
          {!!commitID && (
            <div className="min-w-0 flex items-baseline gap-1">
              <span className="text-muted">Commit:</span>
              <span data-cy="helm-git-commit">
                <GitCommitLink baseURL={repo || ''} commitHash={commitID} />
              </span>
            </div>
          )}
          <div className="min-w-0">
            <span className="text-muted">Auto-update: </span>
            <span data-cy="helm-git-auto-update">
              {autoUpdate ? 'On' : 'Off'}
            </span>
          </div>
          {!!autoUpdateInterval && (
            <div className="min-w-0">
              <span className="text-muted">Interval: </span>
              <span data-cy="helm-git-interval">{autoUpdateInterval}</span>
            </div>
          )}
          {!!webhook && (
            <div className="min-w-0">
              <span className="text-muted">Webhook: </span>{' '}
              <div className="flex items-center gap-2">
                <span data-cy="helm-git-webhook">
                  {truncateLeftRight(webhookUrl)}
                </span>
                <CopyButton
                  copyText={webhookUrl}
                  color="light"
                  data-cy="copy-webhook-link-button"
                >
                  Copy link
                </CopyButton>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
